import { Autocomplete, Box, Grid, TextField } from "@mui/material";
import { Vocabulary } from "../../Utils/Vocabulary";
import { Fragment, useContext, useEffect, useRef, useState } from "react";
import { CRUDContext } from "./ClientsTable";
import { AxiosResponse } from "axios";
import { endpoints } from "../../Utils/UrlEnum";
import SpecialDivider from "../SpecialDivider";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { updateData } from "../../Services/updateData";
import { postData } from "../../Services/postData";
import { ClientFormProps } from "../../Utils/Types";
import { FormsIds } from "../../Utils/Constants";
import Config from "../../Utils/Config";
import styles from "../../Styles/clientPanel.module.css";
import { WebSocketContext } from "../../Context/WebSocketContext";
import { ClientValidationSchema } from "../../Utils/Models";
import {
  getPreviewPaths,
  getValueForAutocomplete,
  onFormError,
} from "../../Utils/Utils";
import DropzoneComponent from "../DropzoneComponent";
import { getData } from "../../Services/getData";

export default function ClientForm(props: ClientFormProps) {
  const clientContext: any = useContext(CRUDContext);
  const [workPoints, setWorkPoints] = useState([]);
  const ref: any = useRef();
  const firstUpdate = useRef(true);
  const websocketContext = useContext(WebSocketContext);
  const {
    register,
    control,
    handleSubmit,
    formState,
    reset,
    watch,
  } = useForm({
    defaultValues: {
      phone: "",
    },
    resolver: yupResolver(ClientValidationSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    context: undefined,
    shouldFocusError: true,
    shouldUnregister: false,
    criteriaMode: "firstError",
  });

  /**
   *
   */
  useEffect(() => {
    if (clientContext?._id && clientContext?._id !== "")
      reset({
        ...clientContext,
        phone: clientContext?.phone,
      });
  }, [clientContext, reset, workPoints]);

  /**
   * Fetch vehicles
   */
  useEffect(() => {
    const fetchWorkPoints = async () => {
      const url = `${endpoints.workPoints.main}/paginated?page=0&perPage=100000`;
      const res = (await getData(url)) as any;
      if (!res || res?.error) {
        return;
      }
      setWorkPoints(res.data.results);
    };
    fetchWorkPoints();
  }, []);

  /**
   *
   */
  useEffect(() => {
    if (props.selectedClient?.CNP && props.selectedClient?.CNP !== "") {
      reset({ ...props.selectedClient, phone: props.selectedClient?.phone });
    }
  }, []);

  /**
   *
   */
  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }
    if (!firstUpdate.current) ref.current.submit();
  }, []);

  /**
   *
   * @param id
   */
  const updateElement = (model: any) => {
    const url = `${endpoints.client.main}/${model?._id}`;
    const formData = new FormData();

    if (model.ci instanceof File) {
      formData.append("ci", model.ci);
    }

    // Append the rest of your model fields.
    Object.keys(model).forEach((key) => {
      if (key !== "ci") {
        if (model[key] instanceof Array || model[key] instanceof Object)
          formData.append(key, JSON.stringify(model[key]));
        else formData.append(key, model[key]);
      }
    });

    updateData(url, formData).then((res: AxiosResponse) => {
      if (res && res.data) {
        websocketContext.setShouldUpdate(!websocketContext.shouldUpdate);
        if (props.onSuccess) props.onSuccess(null, res.data);
        props.onClose();
      }
    });
  };

  /**
   *
   * @param id
   */
  const createElement = (model: any) => {
    const url = `${endpoints.client.main}`;
    const formData = new FormData();

    if (model.ci instanceof File) {
      formData.append("ci", model.ci);
    }

    // Append the rest of your model fields.
    Object.keys(model).forEach((key) => {
      if (key !== "ci") {
        if (model[key] instanceof Array || model[key] instanceof Object)
          formData.append(key, JSON.stringify(model[key]));
        else formData.append(key, model[key]);
      }
    });

    postData(url, formData).then((res: AxiosResponse) => {
      if (!res || res?.data.errors) {
        return;
      }
      websocketContext.setShouldUpdate(!websocketContext.shouldUpdate);
      if (props.onSuccess) props.onSuccess(null, res.data);
      props.onClose();
    });
  };

  /**
   *
   * @param model
   */
  const onSubmit = (model: any) => {
    model._id ? updateElement(model) : createElement(model);
  };
  return (
    <Fragment>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleSubmit(onSubmit, onFormError)(e);
        }}
        method="get"
        ref={ref}
        id={FormsIds.clients}
        className={styles.clientFormContainer}
      >
        <Grid container spacing={Config.standardGridSpacing}>
          <Grid item xs={12} sm={12} md={6}>
            <TextField
              {...register("firstName")}
              id="firstName"
              name="firstName"
              label={Vocabulary.firstName}
              fullWidth
              error={typeof formState.errors.firstName === "object"}
              helperText={formState.errors.firstName?.message?.toString()}
              variant="outlined"
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6}>
            <TextField
              {...register("lastName")}
              id="lastName"
              name="lastName"
              label={Vocabulary.lastName}
              fullWidth
              error={typeof formState.errors.lastName === "object"}
              helperText={formState.errors.lastName?.message?.toString()}
              variant="outlined"
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6}>
            <TextField
              {...register("CNP")}
              name="CNP"
              id="CNP"
              label={Vocabulary.CNP}
              value={watch("CNP") || ""}
              fullWidth
              disabled
              type="number"
              variant="outlined"
              error={typeof formState.errors.CNP === "object"}
              helperText={formState.errors.CNP?.message?.toString()}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6}>
            <TextField
              {...register("email")}
              variant="outlined"
              name="email"
              id="email"
              label={Vocabulary.email}
              fullWidth
              error={typeof formState.errors.email === "object"}
              helperText={formState.errors.email?.message?.toString()}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>

          <Grid item xs={12} sm={12} md={6}>
            <TextField
              {...register("phone")}
              variant="outlined"
              name="phone"
              id="phone"
              label={Vocabulary.phone}
              fullWidth
              error={typeof formState.errors.phone === "object"}
              helperText={formState.errors.phone?.message?.toString()}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>

          <Grid item xs={12} sm={12} md={6}>
            <Controller
              name="workPointId"
              control={control}
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <Autocomplete
                  freeSolo={false}
                  disablePortal
                  disableClearable
                  clearOnBlur={true}
                  options={workPoints}
                  isOptionEqualToValue={(option: any, value: any) =>
                    option?._id === value?._id || option?._id === value
                  }
                  getOptionLabel={(option: any) => `${option?.name}` || ""}
                  onChange={(event, value: any) => {
                    field.onChange(value);
                  }}
                  value={getValueForAutocomplete(workPoints, field.value)}
                  renderInput={(params) => (
                    <TextField
                      value={field?.value || " "}
                      error={!!error}
                      helperText={error?.message}
                      label={Vocabulary.workPoint}
                      name="workPointId"
                      type="search"
                      {...params}
                    />
                  )}
                />
              )}
            />
          </Grid>

          {/* ///////////////////////////////////////////////////////////////////////////CI */}

          <Grid item xs={12} sm={12} md={12}>
            <SpecialDivider title={Vocabulary.CI} />
          </Grid>
          <Grid item xs={5}>
            <Box
              sx={{
                height: "260px",
                display: "flex",
                alignItems: "center",
                marginBottom: "5px",
                overflow: "hidden", // Prevents any resizing
              }}
            >
              <Controller
                control={control}
                name={`ci` as const}
                defaultValue={""}
                render={({ field }) => (
                  <DropzoneComponent
                    key={
                      Array.isArray(field.value) ? field.value.join(",") : ""
                    }
                    altText="CI"
                    acceptedFileTypes={{
                      "image/jpeg": "",
                      "image/png": "",
                    }}
                    onSave={(files: File[]) => {
                      if (files.length > 0) {
                        field.onChange(files[0]); // Store new file object
                      }
                    }}
                    paths={getPreviewPaths(field.value)}
                    isPreview={true}
                    multiple={false}
                    maxFiles={1}
                  />
                )}
              />
            </Box>
          </Grid>

          {/* ///////////////////////////////////////////////////////////////////////////ADRESA */}

          <Grid item xs={12} sm={12} md={12}>
            <SpecialDivider title={Vocabulary.address} />
          </Grid>
          <Grid item xs={12} sm={12} md={6}>
            <TextField
              {...register(`address.city`)}
              name="address.city"
              id="city"
              label={Vocabulary.city}
              fullWidth
              variant="outlined"
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={3}>
            <TextField
              {...register(`address.county`)}
              name="address.county"
              id="county"
              label={Vocabulary.county}
              fullWidth
              variant="outlined"
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={3}>
            <TextField
              {...register(`address.country`)}
              name="address.country"
              id="country"
              label={Vocabulary.country}
              fullWidth
              variant="outlined"
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
        </Grid>
      </form>
    </Fragment>
  );
}
